import { Link, useNavigate } from "react-router-dom";
import Navigation from "./navigation.js";
import Footer from "./footer";
import { useState, useEffect } from "react";
import "./events.css";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/events/`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      } else {
        setError('Failed to fetch events');
      }
    } catch (err) {
      setError('Network error while fetching events');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return '#28a745';
      case 'ongoing': return '#007bff';
      case 'completed': return '#6c757d';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(event => event.status.toLowerCase() === filter);

  const renderEventCard = (event) => (
    <div 
      key={event.id} 
      className="event-card-large p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300" 
      onClick={() => window.location.href = `/event-details/${event.id}`} 
      style={{cursor:'pointer'}}
    >
      <div className="event-content flex flex-col h-full">
        <div className="event-image-container mb-4">
          <img 
            src={event.thumbnail ? `${API_URL}${event.thumbnail}` : 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg'} 
            className="event-image w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover rounded-lg" 
            alt={event.name} 
          />
        </div>

        <div className="event-category text-xs sm:text-sm md:text-base font-medium text-blue-600 mb-2">
          {event.category}
        </div>
        
        <h3 className="event-title text-lg sm:text-xl md:text-2xl font-bold mb-3 text-gray-800 line-clamp-2">
          {event.name}
        </h3>
        
        <div className="event-details mt-auto">
          <div className="flex items-center text-sm sm:text-base md:text-lg text-gray-600 mb-3">
            <i className="bi-stopwatch mr-2"></i> 
            {formatDateTime(event.start_date, event.start_time)}
            {event.end_date && (
              <>
                {" "}- {formatDateTime(event.end_date, event.end_time)}
              </>
            )}
          </div>
          
          {event.status && (
            <span 
              className={`event-status-badge-pending event-status-${event.status.toLowerCase()} inline-block text-xs sm:text-sm md:text-base px-3 py-1 rounded-full font-medium`}
            >
              {event.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="">
        <div className="fixed top-0 left-0 right-0 z-50">
        <Navigation />
      </div>

      <div className="hero-section2 px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="text-white text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold py-8">
          Events
        </div>
      </div>

      <div className="max-width full-width px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Filter Section - Keeping your original CSS classes for active state */}
        <div className="events-filter-section mt-6 sm:mt-8 md:mt-10 mb-8 sm:mb-10 md:mb-12">
          <div className="filter-buttons flex flex-wrap gap-2 sm:gap-3 md:gap-4">
            <button 
              className={`filter-btn px-4 py-2 sm:px-5 sm:py-2 md:px-6 md:py-3 text-sm sm:text-base md:text-lg rounded-lg transition-colors duration-300 ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Events
            </button>
            <button 
              className={`filter-btn px-4 py-2 sm:px-5 sm:py-2 md:px-6 md:py-3 text-sm sm:text-base md:text-lg rounded-lg transition-colors duration-300 ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button 
              className={`filter-btn px-4 py-2 sm:px-5 sm:py-2 md:px-6 md:py-3 text-sm sm:text-base md:text-lg rounded-lg transition-colors duration-300 ${filter === 'ongoing' ? 'active' : ''}`}
              onClick={() => setFilter('ongoing')}
            >
              Ongoing
            </button>
            <button 
              className={`filter-btn px-4 py-2 sm:px-5 sm:py-2 md:px-6 md:py-3 text-sm sm:text-base md:text-lg rounded-lg transition-colors duration-300 ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="loading-container flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
            <div className="loading-spinner w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-base sm:text-lg md:text-xl text-gray-600">Loading events...</p>
          </div>
        ) : error ? (
          <div className="error-container flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 text-center">
            <p className="text-base sm:text-lg md:text-xl text-red-500 mb-4">{error}</p>
            <button 
              onClick={fetchEvents} 
              className="retry-btn px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 text-white text-sm sm:text-base md:text-lg rounded-lg hover:bg-blue-700 transition-colors duration-300"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="events-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {filteredEvents.length > 0 ? (
              filteredEvents.map(renderEventCard)
            ) : (
              <div className="no-events col-span-1 sm:col-span-2 lg:col-span-3 flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 text-center">
                <p className="text-base sm:text-lg md:text-xl text-gray-600">No events found for the selected filter.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="section-margin"></div>
      <div className="section-margin"></div>

      <Footer />
    </div>
  );
}

export default Events;